import React, { useState, useEffect } from 'react';
import { Feiertag, CreateFeiertagDTO } from '../../types';
import { feiertagService } from '../../services/feiertag.service';
import { formatDate } from '../../utils/dateUtils';

interface FeiertageDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onUpdate: () => void;
}

const FeiertageDialog: React.FC<FeiertageDialogProps> = ({ isOpen, onClose, onUpdate }) => {
    const [feiertage, setFeiertage] = useState<Feiertag[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form State
    const [datum, setDatum] = useState('');
    const [typ, setTyp] = useState('GANZTAGS');
    const [name, setName] = useState('');

    useEffect(() => {
        if (isOpen) {
            loadFeiertage();
        }
    }, [isOpen]);

    const loadFeiertage = async () => {
        try {
            setLoading(true);
            // Fetch future holidays, or maybe simplified: fetch all? 
            // For manage dialog, maybe better to fetch upcoming + recent?
            // Let's fetch all for now or range? Default usually fetching all might be ok for now, 
            // or we just fetch current year + next year if we had range params. 
            // Service supports params but optional.
            const data = await feiertagService.getAllFeiertage();
            // Sort desc by date
            setFeiertage(data.sort((a, b) => new Date(b.datum as string).getTime() - new Date(a.datum as string).getTime()));
        } catch (err: any) {
            setError('Fehler beim Laden der Feiertage');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!datum) return;

        try {
            setLoading(true);
            setError(null);

            const newFeiertag: CreateFeiertagDTO = {
                datum,
                typ,
                name: name || undefined
            };

            await feiertagService.createFeiertag(newFeiertag);

            // Reset form
            setDatum('');
            setName('');
            setTyp('GANZTAGS');

            await loadFeiertage();
            onUpdate(); // Refresh parent view
        } catch (err: any) {
            setError('Fehler beim Erstellen des Feiertags');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Diesen Feiertag wirklich löschen?')) return;

        try {
            setLoading(true);
            await feiertagService.deleteFeiertag(id);
            await loadFeiertage();
            onUpdate();
        } catch (err: any) {
            setError('Fehler beim Löschen des Feiertags');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-800">Feiertage verwalten</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                            {error}
                        </div>
                    )}

                    {/* Add Form */}
                    <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
                        <h3 className="font-semibold mb-3 text-gray-700">Neuen Feiertag eintragen</h3>
                        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                            <div className="md:col-span-1">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Datum</label>
                                <input
                                    type="date"
                                    required
                                    value={datum}
                                    onChange={(e) => setDatum(e.target.value)}
                                    className="w-full px-2 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div className="md:col-span-1">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Typ</label>
                                <select
                                    value={typ}
                                    onChange={(e) => setTyp(e.target.value)}
                                    className="w-full px-2 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="GANZTAGS">Ganztags</option>
                                    <option value="VORMITTAG">Vormittag</option>
                                    <option value="NACHMITTAG">Nachmittag</option>
                                </select>
                            </div>
                            <div className="md:col-span-1">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Name (Optional)</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="z.B. Neujahr"
                                    className="w-full px-2 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div className="md:col-span-1">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                >
                                    {loading ? '...' : 'Hinzufügen'}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* List */}
                    <div>
                        <h3 className="font-semibold mb-3 text-gray-700">Gespeicherte Feiertage</h3>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Typ</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Aktion</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {feiertage.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="px-4 py-4 text-center text-sm text-gray-500">
                                                Keine Feiertage gefunden.
                                            </td>
                                        </tr>
                                    ) : (
                                        feiertage.map((ft) => (
                                            <tr key={ft.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                                    {formatDate(new Date(ft.datum))}
                                                </td>
                                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                                    {ft.name || '-'}
                                                </td>
                                                <td className="px-4 py-2 whitespace-nowrap text-sm">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${ft.typ === 'GANZTAGS' ? 'bg-orange-100 text-orange-800' : 'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                        {ft.typ}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-medium">
                                                    <button
                                                        onClick={() => handleDelete(ft.id)}
                                                        className="text-red-600 hover:text-red-900"
                                                    >
                                                        Löschen
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeiertageDialog;
